import { Link } from 'react-router-dom';
import { PageTransition, StaggeredReveal } from '../components/common/PageTransition';

const About = () => {
  const founder = {
    name: 'Larry Nyamongo Mecha',
    role: 'Founder, Computer Scientist, Software Engineer, and Product Developer/Inventor',
    bio: 'Larry Nyamongo Mecha is a computer scientist, software engineer, and product developer/inventor with several patents. He is building Farmers HUB to bring trusted technology, practical market intelligence, and scalable digital infrastructure to agriculture.',
    imageUrl: '/images/Larry%20Nyamongo%20Mecha.jpeg',
  };

  return (
    <PageTransition>
      {/* Hero section */}
      <StaggeredReveal delay={0.1}>
        <div className="relative bg-green-800">
          <div className="absolute inset-0">
            <img
              className="object-cover w-full h-full"
              src="https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1950&q=80"
              alt="Farmers working in the field"
            />
            <div className="absolute inset-0 bg-green-800 mix-blend-multiply" />
          </div>
          <div className="relative px-4 py-24 mx-auto max-w-7xl sm:py-32 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              About Us
            </h1>
            <p className="mt-6 max-w-3xl text-xl text-green-100">
              Farmers HUB exists to build trusted agritech products that help farmers, buyers, and agricultural businesses make better decisions, access better markets, and grow with confidence.
            </p>
          </div>
        </div>
      </StaggeredReveal>

      {/* Mission section */}
      <StaggeredReveal delay={0.3}>
        <div className="overflow-hidden py-16 bg-white dark:bg-gray-900 lg:py-24">
          <div className="relative px-4 mx-auto max-w-xl sm:px-6 lg:px-8 lg:max-w-7xl">
            <div className="relative">
              <h2 className="text-3xl font-extrabold tracking-tight leading-8 text-center text-gray-900 dark:text-white sm:text-4xl">
                Empowering the Agricultural Ecosystem
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-xl text-center text-gray-500 dark:text-gray-300">
                Farmers HUB is more than a digital platform. It is a practical agritech ecosystem built to connect agricultural production, market access, trade intelligence, and data-driven decision-making.
              </p>
              <div className="mt-8 max-w-3xl mx-auto text-lg text-gray-500 dark:text-gray-300">
                <p>
                  The vision behind Farmers HUB is to make agriculture more efficient, more transparent, and more profitable by combining strong product thinking with real agricultural needs. By leveraging data, software, and platform design, Farmers HUB helps ensure that:
                </p>
                <ul className="mt-4 list-disc list-inside space-y-2 pl-4">
                  <li><strong>Farmers</strong> gain clearer visibility into planning, pricing, and market opportunities.</li>
                  <li><strong>Buyers and agribusinesses</strong> can discover supply, analyze markets, and transact more efficiently.</li>
                  <li><strong>Institutions and stakeholders</strong> can access structured intelligence that supports better agricultural decisions.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </StaggeredReveal>

      {/* Team section */}
      <StaggeredReveal delay={0.5}>
        <div className="py-16 bg-gray-50 dark:bg-gray-800 sm:py-24">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base font-semibold tracking-wide text-green-600 dark:text-green-400 uppercase">Founder</h2>
              <p className="mt-2 text-3xl font-extrabold tracking-tight leading-8 text-gray-900 dark:text-white sm:text-4xl">
                Meet the founder behind Farmers HUB
              </p>
            </div>

            <div className="mt-12 max-w-5xl mx-auto">
              <StaggeredReveal delay={0.6}>
                <div className="overflow-hidden rounded-3xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700">
                  <div className="grid gap-0 md:grid-cols-[320px_1fr]">
                    <div className="bg-green-50 dark:bg-green-950/40 flex items-center justify-center p-8">
                      <img
                        className="object-cover w-64 h-64 rounded-3xl shadow-lg"
                        src={founder.imageUrl}
                        alt={founder.name}
                      />
                    </div>
                    <div className="p-8 md:p-10">
                      <p className="text-sm font-semibold tracking-wide uppercase text-green-600 dark:text-green-400">
                        Founder Profile
                      </p>
                      <h3 className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white">
                        {founder.name}
                      </h3>
                      <p className="mt-2 text-base font-semibold text-green-700 dark:text-green-300">
                        {founder.role}
                      </p>
                      <p className="mt-5 text-base leading-7 text-gray-600 dark:text-gray-300">
                        {founder.bio}
                      </p>
                      <div className="mt-6 space-y-4 text-gray-600 dark:text-gray-300">
                        <p>
                          He combines technical depth with product vision, focusing on solutions that are not only innovative, but commercially useful, scalable, and trusted by real users. His work sits at the intersection of software, invention, and agricultural transformation.
                        </p>
                        <p>
                          In agriculture, that means building products that solve practical problems: market access, planning, intelligence, coordination, and trust. For customers, Farmers HUB represents dependable technology designed around real agricultural needs. For investors, it represents founder-led product execution with room for category-level growth.
                        </p>
                        <p>
                          Larry&apos;s long-term ambition is to develop standout agritech products that create measurable value across the agricultural ecosystem while building durable intellectual property, strong user trust, and scalable commercial opportunities.
                        </p>
                      </div>
                      <div className="mt-8 grid gap-4 sm:grid-cols-3">
                        <div className="rounded-2xl bg-gray-50 dark:bg-gray-800 p-4">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Technical Foundation</p>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            Computer science and software engineering applied to real-world agricultural systems.
                          </p>
                        </div>
                        <div className="rounded-2xl bg-gray-50 dark:bg-gray-800 p-4">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Product & Invention</p>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            Product developer and inventor with several patents and a focus on defensible innovation.
                          </p>
                        </div>
                        <div className="rounded-2xl bg-gray-50 dark:bg-gray-800 p-4">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Agritech Vision</p>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            Building trusted products that help agriculture become more efficient, informed, and scalable.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </StaggeredReveal>
            </div>
          </div>
        </div>
      </StaggeredReveal>

      {/* Founder vision section */}
      <StaggeredReveal delay={0.7}>
        <div className="overflow-hidden py-16 bg-white dark:bg-gray-900 lg:py-24">
          <div className="relative px-4 mx-auto max-w-xl sm:px-6 lg:px-8 lg:max-w-7xl">
            <div className="relative max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-extrabold tracking-tight leading-8 text-gray-900 dark:text-white sm:text-4xl">
                Why Farmers HUB Stands Out
              </h2>
              <p className="mx-auto mt-4 text-xl text-gray-500 dark:text-gray-300">
                Farmers HUB is built with the conviction that agriculture deserves world-class products, not temporary fixes.
              </p>
              <div className="mt-8 space-y-5 text-lg leading-8 text-gray-500 dark:text-gray-300 text-left">
                <p>
                  The platform is being shaped by a founder who understands both technology creation and product execution. That combination matters because the future of agritech will belong to products that are technically sound, commercially relevant, and deeply trusted by the people who use them.
                </p>
                <p>
                  Farmers HUB is designed to earn that trust by focusing on practical utility: reliable market access, better planning intelligence, structured trade insights, and scalable digital coordination. The goal is not to build noise around agriculture. The goal is to build products that become genuinely useful and difficult to ignore.
                </p>
                <p>
                  That is the standard behind Farmers HUB: thoughtful innovation, strong product discipline, and a long-term commitment to creating agricultural technology that customers can rely on and investors can believe in.
                </p>
              </div>
            </div>
          </div>
        </div>
      </StaggeredReveal>

      {/* Values section */}
      <StaggeredReveal delay={0.8}>
        <div className="overflow-hidden py-16 bg-white dark:bg-gray-900 lg:py-24">
          <div className="relative px-4 mx-auto max-w-xl sm:px-6 lg:px-8 lg:max-w-7xl">
            <div className="relative">
              <h2 className="text-3xl font-extrabold tracking-tight leading-8 text-center text-gray-900 dark:text-white sm:text-4xl">
                Our Values
              </h2>
              <div className="mt-10">
                <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
                  <div className="relative">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Innovation</h3>
                    <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                      We constantly explore new technologies and solutions to improve farming practices and market access.
                    </p>
                  </div>
                  <div className="relative">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Sustainability</h3>
                    <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                      We promote sustainable farming practices that benefit both farmers and the environment.
                    </p>
                  </div>
                  <div className="relative">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Community</h3>
                    <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                      We foster a strong community of farmers, buyers, and agricultural experts.
                    </p>
                  </div>
                  <div className="relative">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">Transparency</h3>
                    <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                      We believe in open communication and transparent market information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StaggeredReveal>

      {/* CTA section */}
      <StaggeredReveal delay={1.0}>
        <div className="bg-green-700 dark:bg-green-900 transition-colors duration-200">
          <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              <span className="block">Ready to join our mission?</span>
              <span className="block text-green-200">Start your journey with us today.</span>
            </h2>
            <div className="flex mt-8 lg:mt-0 lg:flex-shrink-0">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/register"
                  className="inline-flex justify-center items-center px-5 py-3 text-base font-medium text-green-600 bg-white rounded-md border border-transparent hover:bg-green-50"
                >
                  Get started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </StaggeredReveal>
    </PageTransition>
  );
};

export default About; 
